import React from 'react';
import { CheckCheck, Check, Clock, XCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { adminApi } from '../../lib/adminAuth';

const fmt = (v) => (v ? new Date(v).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');
const FILTERS = [['all', 'All'], ['opened', 'Opened'], ['delivered', 'Delivered, not opened'], ['sent', 'Waiting'], ['unknown', 'Unconfirmed'], ['failed', 'Failed']];

function State({ r }) {
  if (r.opened_at) return <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCheck className="w-3.5 h-3.5" /> Opened {fmt(r.opened_at)}</span>;
  if (r.status === 'delivered') return <span className="inline-flex items-center gap-1 text-sky-700"><Check className="w-3.5 h-3.5" /> Delivered</span>;
  if (r.status === 'failed') return <span className="inline-flex items-center gap-1 text-rose-600"><XCircle className="w-3.5 h-3.5" /> Failed{r.error ? ` (${r.error})` : ''}</span>;
  return <span className="inline-flex items-center gap-1 text-slate-500"><Clock className="w-3.5 h-3.5" /> {r.status === 'unknown' ? 'Unconfirmed' : 'Waiting for confirmation'}</span>;
}

export default function DeliveryReport({ campaign, onClose }) {
  const [data, setData] = React.useState(null);
  const [filter, setFilter] = React.useState('all');
  React.useEffect(() => {
    setData(null); setFilter('all');
    if (campaign) adminApi.get(`/api/admin/push/campaigns/${campaign.id}/report`).then((r) => setData(r.data)).catch(() => setData({ rows: [] }));
  }, [campaign]);
  const rows = (data?.rows || []).filter((r) => filter === 'all' || (filter === 'opened' ? r.opened_at : !r.opened_at && r.status === filter));
  return (
    <Dialog open={!!campaign} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="push-report-dialog">
        <DialogHeader><DialogTitle className="truncate">{campaign?.title}</DialogTitle></DialogHeader>
        {!data ? <p className="text-sm text-slate-400">Loading…</p> : (
          <div className="space-y-3">
            <div className="grid grid-cols-4 gap-2 text-center">
              {[['People', data.people], ['Delivered', data.delivered], ['Opened', data.opened], ['Failed', data.failed]].map(([l, v]) => (
                <div key={l} className="rounded-lg bg-slate-50 py-2"><p className="text-lg font-bold text-[#1a1a2e]" data-testid={`push-report-${l.toLowerCase()}`}>{v}</p><p className="text-[10px] uppercase text-slate-400">{l}</p></div>
              ))}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {FILTERS.map(([k, l]) => (
                <button key={k} type="button" onClick={() => setFilter(k)} data-testid={`push-report-filter-${k}`}
                  className={`px-3 h-7 rounded-full text-xs border ${filter === k ? 'bg-[#1a1a2e] text-white border-[#1a1a2e]' : 'border-slate-200 text-slate-600'}`}>{l}</button>
              ))}
            </div>
            <div className="max-h-80 overflow-y-auto divide-y divide-slate-100" data-testid="push-report-rows">
              {rows.map((r) => (
                <div key={`${r.user_type}-${r.user_id}`} className="flex items-center gap-3 py-2 text-sm" data-testid={`push-report-row-${r.user_id}`}>
                  <span className="flex-1 truncate text-slate-800">{r.name}</span>
                  <span className="text-[10px] uppercase text-slate-400 w-16">{r.user_type}</span>
                  <span className="text-xs w-56 text-right"><State r={r} /></span>
                </div>
              ))}
              {!rows.length && <p className="py-3 text-sm text-slate-400">No one here.</p>}
            </div>
            <p className="text-[11px] text-slate-400">{'"Delivered" means Google/Apple accepted it for the phone. "Opened" needs the updated app.'}</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
